import { useState } from 'react';
import { FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountApi, type AccountMfa } from '@/api/account';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useActionError, type ActionError } from '@/components/useActionError';

export interface MfaSectionProps {
  mfa: AccountMfa;
  onChanged: (mfa: AccountMfa) => void;
}

/**
 * `GET /api/account/mfa`; `POST /mfa/toggle { enabled }` — offered ONLY when `policy` is
 * `OPTIONAL` (anything else answers `409 MFA_TOGGLE_NOT_ALLOWED`), so the toggle is hidden
 * otherwise and the status is shown read-only. Turning it off is confirmed first (it lowers the
 * sign-in to a password alone); the switch only changes after the confirm. Turning it on is one click.
 */
export function MfaSection({ mfa, onChanged }: MfaSectionProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const [busy, setBusy] = useState(false);
  // Shown with its Reference (TMMRSL-0072).
  const [error, setError] = useState<ActionError | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const optional = mfa.policy === 'OPTIONAL';

  async function apply(enabled: boolean) {
    await accountApi.toggleMfa(enabled);
    onChanged(await accountApi.mfa());
  }

  async function turnOn() {
    setBusy(true);
    setError(null);
    try {
      await apply(true);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" component="h2" sx={{ mb: 0.5 }}>
        {t('account.mfa.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('account.mfa.lead')}
      </Typography>
      <ActionErrorAlert error={error} sx={{ mb: 2 }} />
      {optional ? (
        <FormControlLabel
          control={
            <Switch
              checked={mfa.mfaEnabled}
              disabled={busy}
              onChange={(e) => (e.target.checked ? void turnOn() : setConfirmOff(true))}
            />
          }
          label={t('account.mfa.toggle')}
        />
      ) : (
        <Typography variant="body2">
          {mfa.mfaEnabled ? t('account.mfa.statusOn') : t('account.mfa.statusOff')}{' '}
          {t('account.mfa.managed')}
        </Typography>
      )}
      <ConfirmDialog
        open={confirmOff}
        title={t('account.mfa.offTitle')}
        consequence={t('account.mfa.offBody')}
        confirmLabel={t('account.mfa.offConfirm')}
        destructive
        onConfirm={() => apply(false)}
        onCancel={() => setConfirmOff(false)}
        errorMessage={(err) => describe(err).message}
      />
    </Paper>
  );
}
